/**
 * Recordings API Client
 * REST API operations for managing sales call recordings
 */

import { kyClient } from '@/shared/api';
import type {
  Recording,
  RecordingsResponse,
  CreateRecordingRequest,
  UpdateRecordingRequest,
} from '../types/recording';

const API_BASE = import.meta.env.VITE_AI_API_URL || 'https://ai-api-dev.auctuscognita.ai';

/**
 * List all recordings for the current user
 * @param page - Page number (default: 1)
 * @param pageSize - Items per page (default: 20)
 * @returns Paginated list of recordings
 */
export async function listRecordings(
  page = 1,
  pageSize = 20
): Promise<RecordingsResponse> {
  try {
    const response = await kyClient
      .get(`${API_BASE}/api/recordings`, {
        searchParams: {
          page: page.toString(),
          pageSize: pageSize.toString(),
        },
        timeout: 10000,
      })
      .json<RecordingsResponse>();

    // Parse dates from ISO strings
    response.recordings = response.recordings.map((recording) => ({
      ...recording,
      uploadedAt: new Date(recording.uploadedAt),
      updatedAt: new Date(recording.updatedAt),
    }));

    return response;
  } catch (error) {
    console.error('Failed to list recordings:', error);
    throw new Error('Failed to fetch recordings');
  }
}

/**
 * Get a single recording by ID
 * @param id - Recording ID
 * @returns Recording details
 */
export async function getRecording(id: string): Promise<Recording> {
  try {
    const response = await kyClient
      .get(`${API_BASE}/api/recordings/${id}`, {
        timeout: 10000,
      })
      .json<Recording>();

    // Parse dates from ISO strings
    return {
      ...response,
      uploadedAt: new Date(response.uploadedAt),
      updatedAt: new Date(response.updatedAt),
    };
  } catch (error) {
    console.error(`Failed to get recording ${id}:`, error);
    throw new Error('Failed to fetch recording details');
  }
}

/**
 * Create a new recording
 * @param data - Recording creation data
 * @returns Created recording
 */
export async function createRecording(
  data: CreateRecordingRequest
): Promise<Recording> {
  try {
    const response = await kyClient
      .post(`${API_BASE}/api/recordings`, {
        json: data,
        timeout: 15000,
      })
      .json<Recording>();

    // Parse dates from ISO strings
    return {
      ...response,
      uploadedAt: new Date(response.uploadedAt),
      updatedAt: new Date(response.updatedAt),
    };
  } catch (error) {
    console.error('Failed to create recording:', error);
    throw new Error('Failed to save recording metadata');
  }
}

/**
 * Update a recording
 * @param id - Recording ID
 * @param data - Fields to update
 * @returns Updated recording
 */
export async function updateRecording(
  id: string,
  data: UpdateRecordingRequest
): Promise<Recording> {
  try {
    const response = await kyClient
      .patch(`${API_BASE}/api/recordings/${id}`, {
        json: data,
        timeout: 10000,
      })
      .json<Recording>();

    // Parse dates from ISO strings
    return {
      ...response,
      uploadedAt: new Date(response.uploadedAt),
      updatedAt: new Date(response.updatedAt),
    };
  } catch (error) {
    console.error(`Failed to update recording ${id}:`, error);
    throw new Error('Failed to update recording');
  }
}

/**
 * Delete a recording
 * @param id - Recording ID
 */
export async function deleteRecording(id: string): Promise<void> {
  try {
    await kyClient.delete(`${API_BASE}/api/recordings/${id}`, {
      timeout: 10000,
    });
  } catch (error) {
    console.error(`Failed to delete recording ${id}:`, error);
    throw new Error('Failed to delete recording');
  }
}
